import React, { useContext } from "react";
import { UserContext } from "../../../context/UserContext";
import { boardTheme } from "./heroTheme";
import styles from "./pharmacyBoard.module.css";

/**
 * The shared pieces of the pharmacy board, used by both the Dashboard and
 * Analytics & Reports so the two screens read as one product - the gradient
 * band that leads each screen, and the cards below it.
 */

/**
 * Wraps a screen, carrying the organisation's derived brand ramp down to
 * everything inside it. Every board screen starts with this.
 */
export const Board = ({ children }) => {
  const { userDetails } = useContext(UserContext);
  const theme = boardTheme(userDetails?.activeOrg?.color);

  return (
    <div className={styles.board} style={theme}>
      {children}
    </div>
  );
};

/**
 * The band that leads every board screen: context, headline and actions on the
 * left, the figures on the right.
 *
 * `metrics` are `{ label, value, onClick }`. A metric with an onClick renders
 * as a link - underlined, keyboard reachable - because the figures on this band
 * are the quickest way into the list behind them.
 */
export const HeroBand = ({ kicker, headline, sub, actions = [], metrics = [] }) => (
  <div className={styles.hero}>
    <div className={styles.heroIn}>
      <div className={styles.heroL}>
        {kicker ? <div className={styles.hkick}>{kicker}</div> : null}
        <div className={styles.hgreet}>{headline}</div>
        {sub ? <div className={styles.hsub}>{sub}</div> : null}

        {actions.length ? (
          <div className={styles.hcta}>
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                className={`${styles.hbtn} ${action.solid ? styles.hbtnSolid : ""}`}
                onClick={action.onClick}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className={styles.hmetrics}>
        {metrics.map((metric) => (
          <div key={metric.label}>
            <div className={styles.hmlabel}>{metric.label}</div>
            <div className={styles.hmval}>
              {metric.onClick ? (
                <button
                  type="button"
                  className={styles.hmlink}
                  onClick={metric.onClick}
                  title={`View ${metric.label.toLowerCase()}`}
                >
                  {metric.value}
                </button>
              ) : (
                metric.value
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/** A titled card, with the optional count pill the mockup puts beside the title. */
export const BoardCard = ({ title, count, children }) => (
  <section className={styles.card}>
    <div className={styles.cardHead}>
      <h3 className={styles.cardTitle}>{title}</h3>
      {count != null ? <span className={styles.cardCount}>{count}</span> : null}
    </div>
    <div className={styles.cardBody}>{children}</div>
  </section>
);

/**
 * The board's two columns. Each card follows the one above it in its own
 * column, so a short card never leaves a gap before the next one down.
 */
export const BoardColumns = ({ children }) => (
  <div className={styles.columns}>{children}</div>
);

export const BoardColumn = ({ children }) => (
  <div className={styles.column}>{children}</div>
);

export const BoardEmpty = ({ children }) => (
  <div className={styles.empty}>{children}</div>
);

export default Board;
